import { LogisticsProjectLookupComponent } from '../../../logistics-projects/logistics-project-lookup.component';
import { Component, OnInit } from '@angular/core';
import { MenuItem, MessageService } from 'primeng/api';
import { TableLazyLoadEvent, TableModule } from 'primeng/table';
import * as XLSX from 'xlsx';
import { finalize } from 'rxjs/operators'; // <-- Add this import
import { SessionStorageService } from "../../../services/session-storage.service";
import { AccountingReportsService, ReportDiagnostic } from "../../accounting-reports.service";
import { ProfitAndLossItem } from "./profit-and-loss-item.model";
import { ChartOfAccountsService } from "../../../chart-of-accounts/chart-of-accounts.service";
import { forkJoin } from "rxjs";
import { ChartOfAccounts } from "../../../chart-of-accounts/chart-of-accounts.model";
import { ProfitAndLoss } from "./profit-and-loss.model";
import { CurrencyPipe, CommonModule } from "@angular/common";
import { FormsModule } from '@angular/forms';
import { BreadcrumbModule } from 'primeng/breadcrumb';
import { SelectModule } from 'primeng/select';
import { ButtonModule } from 'primeng/button';
import { DatePickerModule } from 'primeng/datepicker';
import { CardModule } from 'primeng/card';
import { ToastModule } from "primeng/toast";
import { ProgressBarModule } from "primeng/progressbar";
import { DividerModule } from "primeng/divider";
import { IftaLabelModule } from 'primeng/iftalabel';
import { PageDataPanelComponent } from '../../../shared/page-layout/page-data-panel.component';
import { PageFormSectionComponent } from '../../../shared/page-layout/page-form-section.component';
import { PageHeroComponent } from '../../../shared/page-layout/page-hero.component';
import { PageShellComponent } from '../../../shared/page-layout/page-shell.component';
import { ReportEmailComponent } from '../shared/report-email.component';
import { buildAccountingReportPeriodLabels } from '../shared/accounting-report-periods';
import { AccountingReportTreeCellComponent } from '../shared/accounting-report-tree-cell.component';
import { closeFinancialReportPdf, downloadFinancialReportPdf, openFinancialReportPdf } from '../shared/financial-report-pdf';
import { PendingActionTracker } from '../../../shared/state/pending-action-tracker';

@Component({
    selector: 'app-profit-and-loss',
    templateUrl: './profit-and-loss.component.html',
    styleUrls: [
        './profit-and-loss.component.css',
        '../shared/accounting-report-tree-table.css'
    ],
    standalone: true,
    imports: [
        LogisticsProjectLookupComponent,
        CommonModule,
        FormsModule,
        BreadcrumbModule,
        SelectModule,
        ButtonModule,
        DatePickerModule,
        CardModule,
        TableModule,
        DatePickerModule,
        SelectModule,
        ToastModule,
        ProgressBarModule,
        DividerModule,
        IftaLabelModule,
        PageShellComponent,
        PageHeroComponent,
        PageFormSectionComponent,
        PageDataPanelComponent,
        ReportEmailComponent,
        AccountingReportTreeCellComponent
    ],
    providers: [
        MessageService,
        AccountingReportsService,
        ChartOfAccountsService, // <-- Add this
        CurrencyPipe,
        SessionStorageService
    ]

})
export class ProfitAndLossComponent implements OnInit {
    readonly reportActions = new PendingActionTracker<string>();

    isLoading: boolean = false;
    breadcrumbItems: MenuItem[];
    homeBreadcrumb: MenuItem;

    startDate: Date = new Date(new Date().getFullYear(), 0, 1); // Default to start of current year
    endDate: Date = new Date(); // Default to today

    totalRevenue: number = 0;
    totalExpenses: number = 0;
    netProfitLoss: number = 0;
    reportCurrencyCode: string = 'SAR'; // Default currency

    private hierarchicalProfitAndLossItems: ProfitAndLossItem[] = [];
    first: number = 0; // For pagination

    // ADD these lines:
    incomeItems: ProfitAndLossItem[] = [];
    expenseItems: ProfitAndLossItem[] = [];
    totalIncomeRecords = 0;
    totalExpenseRecords = 0;
    diagnostics: ReportDiagnostic[] = [];

    selectedPeriodType: string = 'yearly'; // Default period type
    periodOptions: any[] = [
        { name: 'Yearly', value: 'yearly' },
        { name: 'Quarterly', value: 'quarterly' },
        { name: 'Monthly', value: 'monthly' }
    ];
    selectedPeriods: string[] = []; // e.g., ['2024', '2025'] or ['Q1 2025', 'Q2 2025']

    selectedLogisticsProjectId?: number | null;

    constructor(
        private accountReportService: AccountingReportsService,
        private chartOfAccountsService: ChartOfAccountsService, // <-- Add this
        private messageService: MessageService,
        private currencyPipe: CurrencyPipe,
        private sessionStorageService: SessionStorageService
    ) { }

    ngOnInit(): void {
        this.breadcrumbItems = [{ label: 'Accounts' }, { label: 'Reports' }, { label: 'Profit and Loss' }];
        this.homeBreadcrumb = { icon: 'pi pi-home', routerLink: '/' };

        this.updateSelectedPeriods(); // Initialize periods based on default type

        this.loadProfitAndLossData({}); // Initial data load
    }

    onPeriodTypeChange(): void {
        this.updateSelectedPeriods();
        this.loadProfitAndLossData({});
    }

    updateSelectedPeriods(): void {
        this.selectedPeriods = buildAccountingReportPeriodLabels(
            this.startDate,
            this.endDate,
            this.selectedPeriodType
        );
    }

    loadProfitAndLossData(event: TableLazyLoadEvent) {
        if (!this.startDate || !this.endDate) {
            this.messageService.add({ severity: 'warn', summary: 'Missing Dates', detail: 'Please select both start and end dates.' });
            return;
        }

        this.updateSelectedPeriods();

        this.isLoading = true;
        this.first = event.first || 0;

        const companyId = this.sessionStorageService.getCompany()?.id;
        if (!companyId) {
            this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Company information is missing.' });
            this.isLoading = false;
            return;
        }

        // Use forkJoin to fetch P&L data and the COA tree in parallel
        forkJoin({
            plData: this.accountReportService.getProfitAndLossV2(
                companyId,
                this.selectedPeriodType,
                this.startDate.getTime(),
                this.endDate.getTime(),
                this.selectedLogisticsProjectId
            ),
            coaTree: this.chartOfAccountsService.getAllChartOfAccountsByCompany(companyId, true)
        }).pipe(
            finalize(() => this.isLoading = false)
        ).subscribe({
            next: ({ plData, coaTree }) => {
                const reportRows = plData.rows ?? [];
                this.diagnostics = plData.diagnostics ?? [];
                if (reportRows.length > 0) {
                    this.reportCurrencyCode = reportRows[0].currencyCode || this.reportCurrencyCode;
                }
                // Filter the COA to only include the roots relevant for a Profit and Loss report.
                // This is the key fix to prevent duplicate or irrelevant rows from appearing.
                const relevantCoaRoots = coaTree.filter(root =>
                    ['Income', 'Revenue', 'Expense', 'Expenses'].includes(root.accountType?.accountCategory ?? '')
                );

                // Transform the flat data into a hierarchical structure using only the relevant parts of the COA.
                this.hierarchicalProfitAndLossItems = this.transformToTree(reportRows, relevantCoaRoots);

                // ADD this line:
                this.splitAndFlattenData();

                this.calculateSummaryTotals();
                this.messageService.add({ severity: 'success', summary: 'Success', detail: 'Profit and Loss data loaded.' });
            },
            error: (err) => {
                console.error('Failed to load Profit and Loss data:', err);
                this.incomeItems = [];
                this.expenseItems = [];
                this.hierarchicalProfitAndLossItems = [];
                this.totalIncomeRecords = 0;
                this.totalExpenseRecords = 0;
                this.diagnostics = [];
                this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to load report data. Please try again.' });
            }
        });
    }

    calculateSummaryTotals(): void {
        this.totalRevenue = 0;
        this.totalExpenses = 0;
        this.netProfitLoss = 0;

        // Use the hierarchical data, which is the source of truth.
        // We only need to sum the root-level items (Income, Expenses) as they already contain the aggregated totals.
        this.hierarchicalProfitAndLossItems.forEach(rootItem => {
            const totalForCategory = Object.values(rootItem.netChanges).reduce((sum, val) => sum + val, 0);

            if (rootItem.plCategory === 'Income') {
                this.totalRevenue += totalForCategory;
            } else if (rootItem.plCategory === 'Expenses' || rootItem.plCategory === 'Cost of Goods Sold') {
                this.totalExpenses += totalForCategory;
            }
        });

        // Net Profit/Loss is simply Revenue - Expenses.
        this.netProfitLoss = this.totalRevenue - this.totalExpenses;
    }

    /**
     * Calculates the net profit (or loss) for a single given period.
     * Note: This is not currently used in the template but is fixed here for future utility.
     * @param period The period label (e.g., '2024', 'Q1 2025')
     * @returns The net profit/loss for that period.
     */
    getTotalNetProfitLossForPeriod(period: string): number {
        let incomeTotal = 0;
        let expenseTotal = 0;

        // Use the hierarchical data source for efficiency
        this.hierarchicalProfitAndLossItems.forEach(rootItem => {
            const periodValue = rootItem.netChanges[period] || 0;
            if (rootItem.plCategory === 'Income') {
                incomeTotal += periodValue;
            } else if (rootItem.plCategory === 'Expenses' || rootItem.plCategory === 'Cost of Goods Sold') {
                expenseTotal += periodValue;
            }
        });

        return incomeTotal - expenseTotal;
    }

    exportExcel(): void {
        if (this.hierarchicalProfitAndLossItems.length === 0) {
            this.messageService.add({ severity: 'warn', summary: 'No Data', detail: 'No data to export.' });
            return;
        }

        const dataToExport: any[][] = [];

        // 1. Add the high-level summary at the top, just like the UI
        dataToExport.push(['Profit & Loss Summary']);
        dataToExport.push(['Total Revenue', this.totalRevenue]);
        dataToExport.push(['Total Expenses', this.totalExpenses]);
        dataToExport.push(['Net Profit / Loss', this.netProfitLoss]);
        dataToExport.push([]); // Add a blank row for spacing

        // Define the main header row for the tables
        const tableHeader = ['Account Code', 'Account Name', ...this.selectedPeriods];

        // Reusable helper function to process a section (e.g., Income or Expenses)
        const processSection = (title: 'Income' | 'Expenses') => {
            const sectionTree = this.hierarchicalProfitAndLossItems.filter(item =>
                item.plCategory === title || (title === 'Expenses' && item.plCategory === 'Cost of Goods Sold')
            );
            if (sectionTree.length === 0) {
                return; // Don't create a section if there's no data for it
            }

            dataToExport.push([title]); // Section Title (e.g., "Income")
            dataToExport.push(tableHeader); // Add the column headers

            // Recursive function to flatten the tree data into rows
            const flattenForExport = (items: ProfitAndLossItem[]) => {
                items.forEach(item => {
                    const row: any[] = [
                        item.accountCode,
                        '  '.repeat(item.level) + item.accountName // Indent account name based on level
                    ];
                    // Add the value for each period, or 0 if it doesn't exist
                    this.selectedPeriods.forEach(period => row.push(item.netChanges[period] || 0));
                    dataToExport.push(row);

                    // If the node has children, process them recursively
                    if (item.children) {
                        flattenForExport(item.children);
                    }
                });
            };

            flattenForExport(sectionTree);

            // Add the total row for the section
            const totalRow: any[] = ['', `Total ${title}`]; // Label for the total row
            this.selectedPeriods.forEach(period => {
                // Use the existing function to get the total for the category and period
                totalRow.push(this.getTotalForCategory(title, period));
            });
            dataToExport.push(totalRow);
            dataToExport.push([]); // Add a blank row for spacing after the section
        };

        // 2. Process and add the Income section
        processSection('Income');

        // 3. Process and add the Expenses section
        processSection('Expenses');

        // 4. Generate the Excel file from the prepared data array
        const worksheet: XLSX.WorkSheet = XLSX.utils.aoa_to_sheet(dataToExport);
        const workbook: XLSX.WorkBook = { Sheets: { 'data': worksheet }, SheetNames: ['data'] };
        const excelBuffer: any = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });

        this.saveAsExcelFile(excelBuffer, "ProfitAndLossReport");
        this.messageService.add({ severity: 'success', summary: 'Exported', detail: 'Report exported to Excel.' });
    }

    private saveAsExcelFile(buffer: any, fileName: string): void {
        const data: Blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8' });
        const downloadLink: HTMLAnchorElement = document.createElement('a');
        const url = URL.createObjectURL(data);
        downloadLink.href = url;
        downloadLink.download = fileName + '_' + new Date().toISOString().slice(0, 10) + '.xlsx';
        document.body.appendChild(downloadLink);
        downloadLink.click();
        document.body.removeChild(downloadLink);
        URL.revokeObjectURL(url);
    }

    printReport(download = false): void {
        const action = download ? 'download' : 'print';
        if (!this.reportActions.begin(action)) return;
        const target = download ? null : window.open('', '_blank');
        this.accountReportService.printFinanceReport('profit-and-loss', this.sessionStorageService.getCompany()?.id ?? 0, {
            startDate: this.startDate,
            endDate: this.endDate,
            selectedPeriodType: this.selectedPeriodType,
            logisticsProjectId: this.selectedLogisticsProjectId
        }).pipe(finalize(() => this.reportActions.end(action))).subscribe({
            next: blob => download ? downloadFinancialReportPdf(blob, 'profit-and-loss') : openFinancialReportPdf(blob, target),
            error: () => {
                closeFinancialReportPdf(target);
                this.messageService.add({ severity: 'error', summary: 'Print unavailable', detail: 'Failed to generate the Profit and Loss PDF.' });
            }
        });
    }

    private legacyPrintReport(): void {
        if (this.hierarchicalProfitAndLossItems.length === 0) {
            this.messageService.add({ severity: 'warn', summary: 'No Data', detail: 'There is no data to print.' });
            return;
        }

        // 1. Gather all necessary information
        const company = this.sessionStorageService.getCompany();
        const user = this.sessionStorageService.getCustomer();
        const generationDate = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
        const fileName = 'ProfitAndLossReport_' + new Date().toISOString().slice(0, 10); // <-- Generate filename

        // Helper to safely format currency
        const formatCurrency = (value: number) => {
            return this.currencyPipe.transform(value, this.reportCurrencyCode, 'symbol', '1.2-2') || '';
        };

        // 2. Define the CSS for a clean print layout
        const styles = `
        <style>
            body { font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol"; font-size: 12px; }
            .report-header { display: flex; justify-content: space-between; align-items: center; border-bottom: 2px solid #666; padding-bottom: 10px; margin-bottom: 20px; }
            .report-header .logo { max-height: 50px; }
            .report-header .company-info { text-align: left; }
            .report-header .user-info { display: flex; align-items: center; text-align: right; }
            .report-header .user-info img { width: 40px; height: 40px; border-radius: 50%; margin-left: 10px; }
            .report-title { text-align: center; margin-bottom: 20px; }
            .summary-table, .data-table { width: 100%; border-collapse: collapse; margin-bottom: 25px; }
            .summary-table td { padding: 8px; border: 1px solid #ddd; }
            .data-table th, .data-table td { border: 1px solid #ccc; padding: 6px; text-align: left; }
            .data-table th { background-color: #f2f2f2; font-weight: bold; }
            .data-table .section-title { font-size: 1.2em; font-weight: bold; background-color: #e0e0e0; }
            .data-table .total-row { font-weight: bold; background-color: #f2f2f2; }
            .text-right { text-align: right !important; }
            .font-bold { font-weight: bold; }
            @media print {
                body { -webkit-print-color-adjust: exact; }
            }
        </style>
    `;

        // 3. Build the HTML structure for the report
        let reportHtml = `
        <html>
            <head>
                <title>${fileName}</title>
                ${styles}
            </head>
            <body>
                <div class="report-header">
                    <div class="company-info">
                        <img src="${company?.logo || 'https://placehold.co/150x50'}" alt="Company Logo" class="logo">
                        <h1>${company?.name || 'Your Company'}</h1>
                    </div>
                    <div class="user-info">
                        <div>
                            <strong>Printed By:</strong> ${user?.name || 'N/A'}<br>
                            <strong>Date:</strong> ${generationDate}
                        </div>
                        <img src="${user?.imageUrl || 'https://placehold.co/40x40'}" alt="User Avatar">
                    </div>
                </div>

                <div class="report-title"><h1>Profit and Loss Report</h1></div>

                <table class="summary-table">
                    <tr><td>Total Revenue</td><td class="text-right">${formatCurrency(this.totalRevenue)}</td></tr>
                    <tr><td>Total Expenses</td><td class="text-right">${formatCurrency(this.totalExpenses)}</td></tr>
                    <tr><td class="font-bold">Net Profit / Loss</td><td class="text-right font-bold">${formatCurrency(this.netProfitLoss)}</td></tr>
                </table>
    `;

        // Reusable helper function to build an HTML table for a section
        const buildSectionHtml = (title: 'Income' | 'Expenses'): string => {
            const sectionTree = this.hierarchicalProfitAndLossItems.filter(item =>
                item.plCategory === title || (title === 'Expenses' && item.plCategory === 'Cost of Goods Sold')
            );
            if (sectionTree.length === 0) return '';

            let tableHtml = `<table class="data-table">`;
            // Section Header
            tableHtml += `<thead><tr><th colspan="${2 + this.selectedPeriods.length}" class="section-title">${title}</th></tr>`;
            // Column Headers
            tableHtml += `<tr><th>Account Code</th><th>Account Name</th>`;
            this.selectedPeriods.forEach(p => tableHtml += `<th class="text-right">${p}</th>`);
            tableHtml += `</tr></thead><tbody>`;

            // Data Rows (recursive)
            const buildRows = (items: ProfitAndLossItem[]) => {
                items.forEach(item => {
                    tableHtml += `<tr class="${item.isSummaryRow ? 'font-bold' : ''}">
                                <td style="padding-left: ${item.level * 15 + 6}px;">${item.accountCode}</td>
                                <td>${item.accountName}</td>`;
                    this.selectedPeriods.forEach(p => tableHtml += `<td class="text-right">${formatCurrency(item.netChanges[p] || 0)}</td>`);
                    tableHtml += `</tr>`;
                    if (item.children) buildRows(item.children);
                });
            };
            buildRows(sectionTree);

            // Footer Total Row
            tableHtml += `</tbody><tfoot><tr class="total-row">
                        <td colspan="2">Total ${title}</td>`;
            this.selectedPeriods.forEach(p => tableHtml += `<td class="text-right">${formatCurrency(this.getTotalForCategory(title, p))}</td>`);
            tableHtml += `</tr></tfoot></table>`;
            return tableHtml;
        };

        // Add Income and Expense sections to the report
        reportHtml += buildSectionHtml('Income');
        reportHtml += buildSectionHtml('Expenses');
        reportHtml += `</body></html>`;

        // 4. Open a new window and print the generated HTML
        const printWindow = window.open('', '_blank', 'height=800,width=1000');
        if (printWindow) {
            printWindow.document.write(reportHtml);
            printWindow.document.close();
            printWindow.focus(); // Necessary for some browsers
            setTimeout(() => { // Timeout to ensure content is rendered
                printWindow.print();
                printWindow.close();
            }, 500);
        }

        this.messageService.add({ severity: 'info', summary: 'Print', detail: 'Print dialog initiated.' });
    }

    /**
     * Merges the flat, periodic P&L data from the API with the hierarchical Chart of Accounts tree.
     * @param plData The flat list of ProfitAndLoss records from the API.
     * @param coaTree The hierarchical structure of the Chart of Accounts.
     * @returns A hierarchical array of ProfitAndLossItem ready for display.
     */
    private transformToTree(plData: ProfitAndLoss[], coaTree: ChartOfAccounts[]): ProfitAndLossItem[] {
        // Step 1: Create a flat lookup map from accountCode to the full ChartOfAccounts object.
        const coaCodeMap = new Map<string, ChartOfAccounts>();
        const flattenCoaForLookup = (nodes: ChartOfAccounts[]) => {
            for (const node of nodes) {
                coaCodeMap.set(node.accountCode, node);
                if (node.childrenAccounts?.length) {
                    flattenCoaForLookup(node.childrenAccounts);
                }
            }
        };
        flattenCoaForLookup(coaTree);

        // Step 2: Aggregate the flat P&L data.
        const aggregatedDataMap = new Map<number, Partial<ProfitAndLossItem>>();

        for (const item of plData) {
            const coaNode = coaCodeMap.get(item.accountCode);
            if (!coaNode) {
                console.warn(`P&L item with code ${item.accountCode} not found in Chart of Accounts.`, item);
                continue;
            }

            const accountId = coaNode.accountId;

            if (!aggregatedDataMap.has(accountId)) {
                aggregatedDataMap.set(accountId, {
                    accountId: accountId,
                    accountCode: item.accountCode,
                    accountName: item.accountName,
                    plCategory: item.plCategory,
                    plSortOrder: item.plSortOrder,
                    currencyCode: item.currencyCode,
                    netChanges: {}
                });
            }
            const entry = aggregatedDataMap.get(accountId)!;
            const directNetChange = item.directNetChange ?? item.netChange;
            entry.netChanges![item.periodLabel] = (entry.netChanges![item.periodLabel] || 0) + directNetChange;
        }

        // Step 3: Recursively build the tree.
        const buildNode = (coaNode: ChartOfAccounts, level: number): ProfitAndLossItem | null => {
            const aggregatedItemData = aggregatedDataMap.get(coaNode.accountId);

            const treeNode: ProfitAndLossItem = {
                accountId: coaNode.accountId,
                accountCode: coaNode.accountCode,
                accountName: coaNode.accountName,
                level: level,
                isSummaryRow: coaNode.childrenAccounts && coaNode.childrenAccounts.length > 0,
                expanded: level < 1,
                children: [],
                // Initially, use data if available, otherwise set a fallback. This will be corrected for summary rows.
                plCategory: aggregatedItemData?.plCategory || 'Uncategorized',
                plSortOrder: aggregatedItemData?.plSortOrder || 999,
                currencyCode: aggregatedItemData?.currencyCode || this.reportCurrencyCode,
                netChanges: aggregatedItemData?.netChanges || {},
            };

            if (coaNode.childrenAccounts?.length) {
                treeNode.children = coaNode.childrenAccounts
                    .map(childCoa => buildNode(childCoa, level + 1))
                    .filter((childNode): childNode is ProfitAndLossItem => childNode !== null);
            }

            if (treeNode.isSummaryRow) {
                // Only synthetic grouping nodes need to inherit their classification.
                // A summary account can also receive journal postings directly, in which
                // case its API classification remains the source of truth.
                const firstValidChild = treeNode.children?.find(c => c.plCategory !== 'Uncategorized');
                if (!aggregatedItemData && firstValidChild) {
                    treeNode.plCategory = firstValidChild.plCategory;
                    treeNode.plSortOrder = firstValidChild.plSortOrder;
                }

                // Make activity posted directly to a summary account visible so users can
                // reconcile the displayed roll-up from the rows underneath it.
                const ownNetChanges = { ...treeNode.netChanges };
                const hasDirectActivity = Object.values(ownNetChanges).some(value => value !== 0);
                if (hasDirectActivity) {
                    const directPostingRow: ProfitAndLossItem = {
                        accountId: -coaNode.accountId,
                        accountCode: '',
                        accountName: `Direct postings to ${coaNode.accountName}`,
                        level: level + 1,
                        isSummaryRow: false,
                        isDirectPostingRow: true,
                        expanded: false,
                        children: [],
                        plCategory: treeNode.plCategory,
                        plSortOrder: treeNode.plSortOrder,
                        currencyCode: treeNode.currencyCode,
                        netChanges: ownNetChanges
                    };
                    treeNode.children = [directPostingRow, ...(treeNode.children ?? [])];
                }

                const allPeriods = new Set<string>();
                treeNode.children?.forEach(child => {
                    Object.keys(child.netChanges).forEach(period => allPeriods.add(period));
                });

                allPeriods.forEach(period => {
                    const childTotal = treeNode.children?.reduce(
                        (sum, child) => sum + (child.netChanges[period] || 0),
                        0
                    ) ?? 0;
                    treeNode.netChanges[period] = childTotal;
                });
            }

            // Prune branches that have no financial data for any period.
            const hasData = Object.values(treeNode.netChanges).some(val => val !== 0);
            return hasData ? treeNode : null;
        };

        // Start the process by calling buildNode for each root account with level 0.
        return coaTree
            .map(rootCoaNode => buildNode(rootCoaNode, 0))
            .filter((rootNode): rootNode is ProfitAndLossItem => rootNode !== null)
            .sort((a, b) => a.plSortOrder - b.plSortOrder);
    }

    /**
     * Flattens the hierarchical data for rendering in a standard p-table.
     */
    private flattenTreeForDisplay(nodes: ProfitAndLossItem[]): ProfitAndLossItem[] {
        const flattened: ProfitAndLossItem[] = [];
        const flatten = (items: ProfitAndLossItem[]) => {
            for (const node of items) {
                flattened.push(node);
                if (node.children && node.expanded) {
                    flatten(node.children);
                }
            }
        };
        flatten(nodes);
        return flattened;
    }

    /**
     * Splits the main hierarchical data into separate income and expense trees,
     * then flattens them for display in their respective tables.
     */
    private splitAndFlattenData(): void {
        const incomeTree = this.hierarchicalProfitAndLossItems.filter(item => item.plCategory === 'Income');
        const expenseTree = this.hierarchicalProfitAndLossItems.filter(item =>
            item.plCategory === 'Expenses' || item.plCategory === 'Cost of Goods Sold'
        );

        this.incomeItems = this.flattenTreeForDisplay(incomeTree);
        this.expenseItems = this.flattenTreeForDisplay(expenseTree);

        this.totalIncomeRecords = this.incomeItems.length;
        this.totalExpenseRecords = this.expenseItems.length;
    }

    /**
     * Toggles the expanded state of a node and re-calculates the flattened
     * arrays for both tables to reflect the change.
     * @param item The ProfitAndLossItem to expand or collapse.
     */
    toggleNode(item: ProfitAndLossItem): void {
        item.expanded = !item.expanded;
        this.splitAndFlattenData();
    }

    getBalanceSide(value: number | null | undefined, category: string): 'DR' | 'CR' | 'ZERO' {
        const amount = Number(value ?? 0);
        if (Math.abs(amount) < 0.005) {
            return 'ZERO';
        }

        const normalSide = category === 'Income' ? 'CR' : 'DR';
        if (amount > 0) {
            return normalSide;
        }
        return normalSide === 'DR' ? 'CR' : 'DR';
    }

    /**
     * Calculates the total for a given P&L category and period.
     * Used for the table footers.
     * @param category 'Income' or 'Expenses'
     * @param period The period label (e.g., '2024', 'Q1 2025')
     * @returns The total sum for that category and period.
     */
    getTotalForCategory(category: 'Income' | 'Expenses', period: string): number {
        const relevantTree = this.hierarchicalProfitAndLossItems.filter(item =>
            item.plCategory === category || (category === 'Expenses' && item.plCategory === 'Cost of Goods Sold')
        );
        // We sum the root items of the tree, as they already contain the aggregated totals of their children.
        return relevantTree.reduce((sum, item) => sum + (item.netChanges[period] || 0), 0);
    }
}
